/**
 * live-preview-tidy.js — layout by typographic rule, and the accent check,
 * both in the browser, both without a network call.
 *
 * WHY THIS FILE EXISTS. The text helper's v2 (tools/text-assist/SPEC_v2.md,
 * Stelios 2026-09-15) does two of its three jobs locally: it lays the
 * customer's text out on the cover («Τακτοποίησέ το») and it notices a
 * missing tone before the cover is printed. Neither needs a model — the first
 * is geometry, the second is a rule the print queue already runs
 * (tools/live-preview/accents.py) — and a call would mean sending a personal
 * name somewhere for a job the browser can do in a millisecond.
 *
 * WHAT THE DESIGN PROTOTYPE GOT WRONG, so it cannot happen here. Its tidy
 * flattened the customer's own line breaks first and then re-broke the text
 * by CHARACTER COUNT, blind to the box and the face. Stelios's two failing
 * cases: «Στέλιος & Ευγενία ⏎ 20/08/2040 ⏎ Save the Date» — already right —
 * came back as «… ⏎ 20/08/2040 Save the Date», which the cover then broke as
 * «Save / the Date»; and «Το Ημερολόγιο ⏎ Εγκυμοσύνης της ⏎ Στεφανίας» came
 * back unchanged and silent, because nothing in it knew that «της» must not
 * end a line. Everything below is the answer to those two.
 *
 * PURE ON PURPOSE. No DOM here. The caller hands in a `measure(str)` — width
 * of a string at 100px in the cover's face — plus the box and the font's size
 * limits, and gets lines back. In the storefront `measure` is a canvas
 * measureText in the overlay's own font; in `tools/text-assist/test_tidy.mjs`
 * it is a table, so the rules are asserted in node with no browser at all.
 *
 * THE RULES (T1–T10 in SPEC_v2.md §2.1), as code:
 *   T1  only breaks and size — the token list is never altered;
 *   T2  the customer's breaks are the starting layout; if they already score
 *       best, tidy() says `already: true` and changes nothing;
 *   T3  units never split (name pair on &/και/+, a date, a short formula):
 *       the gap inside a unit is simply not a candidate;
 *   T4  a date gets a line of its own;
 *   T5  no line ends on a function word (της, του, και, για, με, στην…);
 *   T6  no orphan — a lone short word on the last line;
 *   T7  balance for centred text — the narrowest line ≥ 55% of the widest;
 *   T8  fit the box: lines ≤ the budget, size never under the floor;
 *   T9  widths are measured, never counted;
 *   T10 lowest penalty wins, deterministically; ties go to the layout nearest
 *       the customer's own breaks.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.LivePreviewTidy = api;
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null), function () {
  'use strict';

  // ── Greek letters, tones, syllables ─────────────────────────────────────
  var GREEK_RE = /[Ͱ-Ͽἀ-῿]/;
  var TONED = 'άέήίόύώΐΰΆΈΉΊΌΎΏ';
  var VOWELS = 'αειηουωΑΕΙΗΟΥΩάέήίόύώΆΈΉΊΌΎΏϊϋΐΰΪΫ';
  var TONE_TO_BARE = { 'ά': 'α', 'έ': 'ε', 'ή': 'η', 'ί': 'ι', 'ό': 'ο', 'ύ': 'υ', 'ώ': 'ω', 'ΐ': 'ϊ', 'ΰ': 'ϋ', 'Ά': 'Α', 'Έ': 'Ε', 'Ή': 'Η', 'Ί': 'Ι', 'Ό': 'Ο', 'Ύ': 'Υ', 'Ώ': 'Ω' };
  // Monosyllables that take a tone only when the writer means the tonic one:
  // «η»/«ή», «που»/«πού». Only the sentence decides; this file cannot read it.
  var AMBIGUOUS = { 'η': 1, 'ή': 1, 'που': 1, 'πού': 1, 'πως': 1, 'πώς': 1, 'μου': 1, 'μού': 1, 'σου': 1, 'σού': 1, 'του': 1, 'τού': 1, 'τους': 1, 'τούς': 1, 'μια': 1, 'μιά': 1, 'για': 1, 'γιά': 1 };

  function stripTones(w) {
    var out = '';
    for (var i = 0; i < w.length; i++) out += TONE_TO_BARE[w[i]] || w[i];
    return out;
  }
  function bare(w) { return stripTones(String(w || '')).toLowerCase(); }
  function isGreek(w) { return GREEK_RE.test(w); }
  function hasLowerGreek(w) {
    for (var i = 0; i < w.length; i++) if (GREEK_RE.test(w[i]) && w[i] === w[i].toLowerCase() && w[i] !== w[i].toUpperCase()) return true;
    return false;
  }
  function toneCount(w) { var n = 0; for (var i = 0; i < w.length; i++) if (TONED.indexOf(w[i]) !== -1) n++; return n; }
  // Maximal runs of vowels: a LOWER bound on syllables (a run may hold a
  // diphthong or a synizesis). Under-counting can only make the check miss a
  // flag, never invent one — which is the only direction that matters.
  function vowelRuns(w) {
    var runs = 0, inside = false;
    for (var i = 0; i < w.length; i++) {
      if (VOWELS.indexOf(w[i]) !== -1) { if (!inside) { runs++; inside = true; } }
      else inside = false;
    }
    return runs;
  }

  /**
   * accents(text) → [{ word, kind, detail }] — the print queue's rule, ported.
   * MISSING_TONE: two or more syllables and no tone; DOUBLE_TONE; FINAL_SIGMA;
   * MISSING_FINAL_S: a name in a pair that lost its ς («Ευαγγελία & Σταύρο»).
   * ALL-CAPS, Latin, greeklish and one-syllable words are never flagged.
   *
   * ⚠ IT MUST FLAG EXACTLY WHAT `accents.findings()` FLAGS. Since 2026-10-12
   * the customer's answer to the line under the box travels with the order
   * (`properties[_lp_accents]`), and the print queue prints a word as typed
   * only when the page asked about it. A word only Python flags would be held
   * for the owner again; a word only this file flags would ask the customer a
   * question the print never asks. `tools/live-preview/accents_parity_selftest.py`
   * runs both on one list and fails on any difference.
   */
  // \p{L}, not [^\W\d_]: in JavaScript \w is ASCII even with the u flag, so
  // the Python pattern this is ported from would see no Greek word at all.
  var WORD_RE = /\p{L}+(?:['’‐-―-]\p{L}+)*/gu;
  var PART_RE = /['’‐-―-]+/;
  function checkPart(p) {
    if (!isGreek(p) || !hasLowerGreek(p)) return null;
    if (p.charAt(p.length - 1) === 'σ') return { kind: 'FINAL_SIGMA', detail: 'τελειώνει σε «σ» αντί για «ς»' };
    var tones = toneCount(p);
    if (tones > 1) return { kind: 'DOUBLE_TONE', detail: 'δύο τόνοι στην ίδια λέξη' };
    var runs = vowelRuns(p);
    if (tones === 0 && runs >= 2) {
      if (AMBIGUOUS[bare(p)]) return null;
      return { kind: 'MISSING_TONE', detail: runs + '+ συλλαβές χωρίς τόνο' };
    }
    return null;
  }
  function checkWord(w) {
    var parts = String(w || '').split(PART_RE).filter(Boolean);
    for (var j = 0; j < parts.length; j++) {
      var got = checkPart(parts[j]);
      if (got) return got;
    }
    return null;
  }

  // The name that lost its final ς — accents.py → coordination_findings, rule
  // for rule: a capitalised Greek word of 3+ letters in -ο, IMMEDIATELY beside
  // a coordinator, with a capitalised Greek word on the coordinator's far side,
  // on a line that carries no article or preposition (those make an accusative
  // legitimate: «Για τον Νίκο και τον Γιώργο»).
  var COORDINATORS = { '&': 1, '+': 1, 'και': 1, 'κι': 1, 'κ': 1 };
  var ACCUSATIVE_LICENCE = {};
  ('τον την το τους τις τα στον στην στη στο στους στις στα με για σε από προς μετά χωρίς κατά παρά')
    .split(' ').forEach(function (w) { ACCUSATIVE_LICENCE[w] = 1; });
  var TOKEN_RE = /\p{L}+|[&+]/gu;
  function upperFirst(t) { var c = t.charAt(0); return c !== c.toLowerCase() && c === c.toUpperCase(); }
  function coordinationFindings(text) {
    var out = [];
    var lines = String(text || '').split('\n');
    for (var li = 0; li < lines.length; li++) {
      var toks = lines[li].match(TOKEN_RE) || [];
      var low = toks.map(function (t) { return t.toLowerCase(); });
      if (low.some(function (t) { return ACCUSATIVE_LICENCE[t]; })) continue;
      for (var i = 0; i < toks.length; i++) {
        var t = toks[i];
        if (t.length < 3 || t.charAt(t.length - 1) !== 'ο' || !upperFirst(t) || !isGreek(t)) continue;
        var partner = null;
        if (i > 0 && COORDINATORS[low[i - 1]]) partner = i >= 2 ? toks[i - 2] : null;
        else if (i + 1 < toks.length && COORDINATORS[low[i + 1]]) partner = i + 2 < toks.length ? toks[i + 2] : null;
        if (partner && upperFirst(partner) && isGreek(partner)) {
          out.push({ word: t, kind: 'MISSING_FINAL_S', detail: 'όνομα σε πτώση που δεν ταιριάζει με το ζευγάρι του — μήπως λείπει το τελικό «ς»;' });
        }
      }
    }
    return out;
  }

  // «η σύνταξή σας», «το όνομά μου»: the second tone an enclitic brings sits on
  // the LAST syllable and is the rule, not a slip (accents.py, order #7786).
  var ENCLITICS = {};
  'μου σου του της μας σας τους με σε τον την το τα τις τες'.split(' ').forEach(function (w) { ENCLITICS[w] = 1; });
  function toneOnLastSyllable(w) {
    var runs = [], cur = '';
    for (var i = 0; i < w.length; i++) {
      if (VOWELS.indexOf(w[i]) !== -1) cur += w[i];
      else if (cur) { runs.push(cur); cur = ''; }
    }
    if (cur) runs.push(cur);
    if (!runs.length) return false;
    var last = runs[runs.length - 1];
    for (var k = 0; k < last.length; k++) if (TONED.indexOf(last[k]) !== -1) return true;
    return false;
  }

  function accents(text) {
    var out = [], seen = {};
    var co = coordinationFindings(text);
    for (var c = 0; c < co.length; c++) {
      if (!seen[co[c].word]) { seen[co[c].word] = 1; out.push(co[c]); }
    }
    var lines = String(text || '').split('\n');
    for (var li = 0; li < lines.length; li++) {
      var words = lines[li].match(WORD_RE) || [];
      for (var i = 0; i < words.length; i++) {
        var w = words[i];
        if (seen[w]) continue;
        var got = checkWord(w);
        if (!got) continue;
        if (got.kind === 'DOUBLE_TONE') {
          var nxt = i + 1 < words.length ? words[i + 1].toLowerCase() : '';
          if (ENCLITICS[nxt] && toneOnLastSyllable(w)) continue;
        }
        seen[w] = 1;
        out.push({ word: w, kind: got.kind, detail: got.detail });
      }
    }
    return out;
  }

  // ── Tokens, units, gaps ─────────────────────────────────────────────────
  var DATE_RE = /^(\d{1,2}\s*[./-]\s*\d{1,2}(\s*[./-]\s*\d{2,4})?|(19|20)\d{2})$/;
  var MONTH_RE = /^(Ιαν|Φεβ|Μαρ|Απρ|Μα[ιΐ]|Ιουν|Ιουλ|Αυγ|Σεπ|Οκτ|Νοε|Δεκ|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i;
  // T5 — a line may not end on these (bare, lowercase). The enclitics
  // μου/σου/μας/σας are NOT here: «τον μπαμπά μου» closes a phrase.
  var FUNCTION_WORDS = {};
  'ο η το οι τα της του των τη την τον τους τις στη στην στον στο στα στους στις με για να και & + κι από σε που πες ή προς'.split(' ')
    .forEach(function (w) { FUNCTION_WORDS[w] = 1; });
  var COORD = { '&': 1, '+': 1, 'και': 1, 'κι': 1 };
  // T3 — short formulas that stay on one line (bare, lowercase, ≤ 3 words).
  var FORMULAS = ['save the date', 'ο γαμος μας', 'our wedding', 'με αγαπη', 'η βαπτιση', 'πες μου', 'θα γινω μαμα', 'το ταξιδι'];

  function isCapOrCaps(w) { return /^[A-ZΑ-ΩΆΈΉΊΌΎΏ]/.test(w); }
  function isDateToken(w) { return DATE_RE.test(w.replace(/\s+/g, '')); }

  /** Split text into word tokens; each remembers how many newlines the customer typed after it. */
  function tokenize(text) {
    var t = String(text || '').replace(/\r/g, '');
    var tokens = [];
    var lines = t.split('\n');
    for (var li = 0; li < lines.length; li++) {
      var words = lines[li].split(/[ \t]+/).filter(Boolean);
      for (var wi = 0; wi < words.length; wi++) tokens.push({ w: words[wi], nl: 0 });
      if (tokens.length && li < lines.length - 1) tokens[tokens.length - 1].nl += 1;
    }
    // Merge «12 Μαΐου 2027» into one date token; «30/08/2027» is one already.
    var merged = [];
    for (var i = 0; i < tokens.length; i++) {
      var a = tokens[i], b = tokens[i + 1], c = tokens[i + 2];
      if (b && c && /^\d{1,2}$/.test(a.w) && MONTH_RE.test(b.w) && /^\d{4}$/.test(c.w) && !a.nl && !b.nl) {
        merged.push({ w: a.w + ' ' + b.w + ' ' + c.w, nl: c.nl }); i += 2; continue;
      }
      merged.push(a);
    }
    for (var k = 0; k < merged.length; k++) {
      var w = merged[k].w;
      merged[k].date = isDateToken(w);
      merged[k].fn = !!FUNCTION_WORDS[bare(w)];
      merged[k].coord = !!COORD[bare(w)];
      merged[k].cap = isCapOrCaps(w);
      merged[k].glue = false;   // set by units(): this token may not be separated from the next
    }
    return merged;
  }

  /** T3: mark gaps that live inside a unit. Returns the token list (mutated). */
  function units(tokens) {
    var n = tokens.length;
    // name pair: Cap coord Cap  → glue both gaps
    for (var i = 0; i + 2 < n; i++) {
      if (tokens[i].cap && tokens[i + 1].coord && tokens[i + 2].cap && !tokens[i].date && !tokens[i + 2].date) {
        tokens[i].glue = true; tokens[i + 1].glue = true;
      }
    }
    // short formulas
    var bares = tokens.map(function (t) { return bare(t.w).replace(/[^\p{L}\p{N} ]/gu, ''); });
    for (var f = 0; f < FORMULAS.length; f++) {
      var parts = FORMULAS[f].split(' ');
      for (var s = 0; s + parts.length <= n; s++) {
        var hit = true;
        for (var p = 0; p < parts.length; p++) if (bares[s + p] !== parts[p]) { hit = false; break; }
        if (hit) {
          for (var g = s; g < s + parts.length - 1; g++) tokens[g].glue = true;
          for (var m = s; m < s + parts.length; m++) tokens[m].formula = true;
        }
      }
    }
    return tokens;
  }

  /** The customer's own layout: indices of tokens that end a line. */
  function customerBreaks(tokens) {
    var out = [];
    for (var i = 0; i < tokens.length - 1; i++) if (tokens[i].nl > 0) out.push(i);
    return out;
  }

  /** Lines (arrays of tokens) for a set of break indices. */
  function linesFor(tokens, breaks) {
    var lines = [], cur = [];
    var set = {};
    for (var b = 0; b < breaks.length; b++) set[breaks[b]] = 1;
    for (var i = 0; i < tokens.length; i++) {
      cur.push(tokens[i]);
      if (set[i] && i < tokens.length - 1) { lines.push(cur); cur = []; }
    }
    if (cur.length) lines.push(cur);
    return lines;
  }
  function lineText(line) { return line.map(function (t) { return t.w; }).join(' '); }

  // ── Scoring ─────────────────────────────────────────────────────────────
  //
  // Two kinds of term. VIOLATIONS are the rules (T4–T8): a layout with any
  // of them is wrong and tidy will change it. PREFERENCES (size, punctuation,
  // line count, distance from the customer's breaks) only rank the layouts
  // that are not wrong. The split is what makes T2 honest: a layout the
  // customer typed is left alone when it breaks no rule, however much a
  // different one might "score" — the size term never overrules intent.
  var W = { overflow: 1000, fnEnd: 50, dateNotAlone: 40, orphan: 30, balance: 20, balanceSlope: 40, tooSmall: 25,
            size: 35, punct: 8, perLine: 2, distance: 1 };
  var TOO_SMALL = 0.6;   // rendered at under 60% of the size the best layout reaches → a violation
  var PUNCT_END = /[,.;:!·…»"”)]$/;

  /**
   * The size (px) a layout renders at: the largest that fits both axes,
   * capped by the face's own ceiling. `ctx.measure(str)` = width at 100px.
   */
  function dims(lines, ctx) {
    var lead = ctx.leading || 1.15;
    var byH = ctx.boxH / (lines.length * lead);
    var byW = Infinity;
    for (var i = 0; i < lines.length; i++) {
      var w100 = ctx.measure(lineText(lines[i]));
      if (w100 > 0) byW = Math.min(byW, (ctx.boxW * 100) / w100);
    }
    return { byH: byH, byW: byW, ceil: ctx.ceilPx || Infinity };
  }
  function sizeFor(lines, ctx) {
    var d = dims(lines, ctx);
    return Math.min(d.byH, d.byW, d.ceil);
  }

  function evaluate(lines, ctx, bestSize, customer) {
    var v = [];
    var d = dims(lines, ctx);
    var size = Math.min(d.byH, d.byW, d.ceil);
    var score = 0;
    if (size < (ctx.floorPx || 0) - 0.01) { score += W.overflow; v.push('overflow'); }
    if (ctx.maxLines && lines.length > ctx.maxLines) { score += W.overflow; v.push('too-many-lines'); }
    if (bestSize > 0 && size < TOO_SMALL * bestSize) { score += W.tooSmall; v.push('too-small:' + Math.round((100 * size) / bestSize)); }
    var widths = [];
    for (var i = 0; i < lines.length; i++) {
      var L = lines[i], last = L[L.length - 1], isLast = i === lines.length - 1;
      var txt = lineText(L);
      var w = ctx.measure(txt);
      var dateLine = L.length === 1 && L[0].date;
      var coordLine = L.length === 1 && L[0].coord;      // «&» alone — a device, not a line to balance
      // T5 — unless the customer set «&» alone on this line on purpose
      if (!isLast && last.fn && !(coordLine && customer)) { score += W.fnEnd; v.push('fn-end:' + last.w); }
      // T4
      var hasDate = false;
      for (var k = 0; k < L.length; k++) if (L[k].date) hasDate = true;
      if (hasDate && L.length > 1) { score += W.dateNotAlone; v.push('date-not-alone'); }
      // T6 — a lone short word on the last line (not a date, not a name)
      if (isLast && lines.length > 1 && L.length === 1 && !L[0].date && !L[0].cap && bare(L[0].w).length < 7) { score += W.orphan; v.push('orphan:' + L[0].w); }
      // preference: a break that lands on punctuation reads as the writer's own pause
      if (!isLast && !dateLine && !coordLine && !PUNCT_END.test(txt)) score += W.punct;
      if (!dateLine && !coordLine) widths.push(w);
    }
    // T7 — balance among the lines that carry words
    if (widths.length > 1) {
      var mx = Math.max.apply(null, widths), mn = Math.min.apply(null, widths);
      var ratio = mx > 0 ? mn / mx : 1;
      if (ratio < 0.55) { score += W.balance + W.balanceSlope * ((0.55 - ratio) / 0.55); v.push('unbalanced:' + Math.round(ratio * 100)); }
    }
    // preferences: size, line count
    if (bestSize > 0) score += W.size * (1 - Math.min(size, bestSize) / bestSize);
    score += W.perLine * (lines.length - 1);
    return { score: score, size: size, violations: v };
  }

  /** Every candidate break set: subsets of allowed gaps, up to maxLines − 1 breaks. */
  function candidates(tokens, maxLines) {
    var gaps = [];
    for (var i = 0; i < tokens.length - 1; i++) if (!tokens[i].glue && !tokens[i].fn) gaps.push(i);
    var out = [[]];
    var maxBreaks = Math.max(0, (maxLines || 4) - 1);
    // iterative subsets, bounded: at most 40 gaps and 5 lines keep this trivial
    function rec(start, chosen) {
      if (chosen.length >= maxBreaks) return;
      for (var g = start; g < gaps.length; g++) {
        var next = chosen.concat([gaps[g]]);
        out.push(next);
        rec(g + 1, next);
      }
    }
    if (gaps.length <= 40) rec(0, []);
    return out;
  }

  function distance(a, b) {
    var set = {}, d = 0, i;
    for (i = 0; i < a.length; i++) set[a[i]] = 1;
    for (i = 0; i < b.length; i++) { if (set[b[i]]) delete set[b[i]]; else d++; }
    for (var k in set) if (Object.prototype.hasOwnProperty.call(set, k)) d++;
    return d;
  }

  /**
   * tidy(text, ctx) → { text, changed, already, lines, before, after }
   * ctx: { measure(str)→width@100px, boxW, boxH, leading, ceilPx, floorPx, maxLines }
   * Blank lines separate paragraphs; each is laid out on its own and the blank
   * line is kept (a blank line is space the customer asked for).
   */
  function tidy(text, ctx) {
    var src = String(text || '').replace(/\r/g, '');
    var paras = src.split(/\n[ \t]*\n/);
    if (paras.length > 1) {
      var parts = [], changedAny = false;
      for (var p = 0; p < paras.length; p++) {
        var r = tidy(paras[p], ctx);
        parts.push(r.text);
        if (r.changed) changedAny = true;
      }
      return changedAny ? { text: parts.join('\n\n'), changed: true, already: false, lines: null }
                        : { text: src, changed: false, already: true, lines: null };
    }
    var tokens = units(tokenize(src));
    if (tokens.length < 2) return { text: src, changed: false, already: true, lines: [src.trim()] };
    var own = customerBreaks(tokens);
    var cands = candidates(tokens, ctx.maxLines);
    // the customer's own layout is always a candidate, even with disallowed breaks
    var ownKey = own.join(',');
    var seenOwn = false;
    for (var c = 0; c < cands.length; c++) if (cands[c].join(',') === ownKey) { seenOwn = true; break; }
    if (!seenOwn) cands.push(own);
    // best size across candidates that respect the budget (for the size terms)
    var bestSize = 0, i;
    var evald = [];
    for (i = 0; i < cands.length; i++) {
      var ls = linesFor(tokens, cands[i]);
      if (ctx.maxLines && ls.length > ctx.maxLines && cands[i].join(',') !== ownKey) continue;
      var s = sizeFor(ls, ctx);
      if (s > bestSize && (!ctx.maxLines || ls.length <= ctx.maxLines)) bestSize = s;
      evald.push({ breaks: cands[i], lines: ls });
    }
    var best = null, ownEval = null;
    for (i = 0; i < evald.length; i++) {
      var e = evald[i];
      var isOwn = e.breaks.join(',') === ownKey;
      var ev = evaluate(e.lines, ctx, bestSize, isOwn);
      ev.score += W.distance * distance(e.breaks, own);
      e.ev = ev;
      if (isOwn) ownEval = e;
      if (!best || ev.score < best.ev.score - 1e-9 || (Math.abs(ev.score - best.ev.score) < 1e-9 && e.breaks.length < best.breaks.length)) best = e;
    }
    if (!best || !ownEval) return { text: src, changed: false, already: true, lines: [src.trim()] };
    // T2: a layout that breaks no rule is already tidy — and goes back EXACTLY
    // as typed, spaces and all. Tidy never edits a layout it has nothing to
    // say about, however a different one might rank on the preferences.
    if (ownEval.ev.violations.length === 0) {
      return { text: src, changed: false, already: true, lines: ownEval.lines.map(lineText), before: ownEval.ev, after: ownEval.ev };
    }
    var chosen = best.ev.score < ownEval.ev.score - 1e-9 ? best : ownEval;
    if (chosen === ownEval) return { text: src, changed: false, already: true, lines: ownEval.lines.map(lineText), before: ownEval.ev, after: ownEval.ev };
    var outLines = chosen.lines.map(lineText);
    return { text: outLines.join('\n'), changed: true, already: false, lines: outLines, before: ownEval.ev, after: chosen.ev };
  }

  /** Does the current layout deserve the amber nudge? Exactly when tidy would change it. */
  function needsTidy(text, ctx) {
    return !tidy(text, ctx).already;
  }

  /** Rule-only guess of what the customer typed — for analytics only, never for behaviour. */
  function shapeOf(text) {
    var t = String(text || '').trim();
    if (!t) return 'empty';
    // a product's own formula («Ο γάμος μας», «Save the Date») is not the customer's text
    var toks = units(tokenize(t)).filter(function (x) { return !x.date && !x.coord && !x.formula; });
    var caps = 0, lower = 0;
    for (var i = 0; i < toks.length; i++) {
      if (toks[i].fn) continue;
      if (toks[i].cap) caps++; else lower++;
    }
    return lower === 0 && caps > 0 && caps <= 3 ? 'name' : 'sentence';
  }

  /**
   * What the Worker is told about the box — SPEC_v2 §4.2 I2 — measured at
   * the size the cover will actually draw at. Without `material` that is 85%
   * of the ceiling. With it, the material's widest unbreakable unit (a name
   * pair on «&», a date, a short formula) may be wider than the box at that
   * size; the fit then keeps it whole by shrinking, and every line gains
   * room — so the capacity is measured at THAT size, never below the floor.
   * Found 2026-09-17 on the wedding box: «Ελένη & Μιχάλης» (15 chars) with
   * «at most 10 per line» asked the model the impossible, and Opus answered
   * with nothing but thinking.
   */
  function capacity(ctx, material) {
    var maxLines = Math.max(1, Math.min(6, ctx.maxLines || 4));
    var sample = 'αβγδεζηθικλμνξοπρστυφχψω ';
    var avg100 = ctx.measure(sample) / sample.length;             // avg glyph width @100px
    var sizeAt = 0.85 * (ctx.ceilPx || 40);
    var mat = String(material || '');
    if (mat.trim()) {
      var tk = units(tokenize(mat));
      var widest = 0, group = [];
      for (var i = 0; i < tk.length; i++) {
        group.push(tk[i]);
        if (!tk[i].glue || i === tk.length - 1) { widest = Math.max(widest, ctx.measure(lineText(group))); group = []; }
      }
      if (widest > 0) sizeAt = Math.min(sizeAt, Math.max((ctx.boxW * 100) / widest, ctx.floorPx || 0));
    }
    var perLine = avg100 > 0 ? Math.floor((ctx.boxW * 100) / (avg100 * sizeAt)) : 24;
    perLine = Math.max(8, Math.min(60, perLine));
    var minChars = Math.max(6, Math.round(0.35 * maxLines * perLine));
    return { maxLines: maxLines, maxCharsPerLine: perLine, minChars: minChars };
  }

  /** An idea the model sent: does it fit the cover at or above the floor? Tidy it if it must; null when it cannot. */
  function fitIdea(text, ctx) {
    var t = String(text || '').replace(/\r/g, '').trim();
    if (!t) return null;
    // "fits" = the cover can draw it AS WRITTEN at some size at or above the
    // floor, within the line budget. (The size itself is settled by the
    // caller — see layoutSize(): the storefront lowers the size when the
    // height-driven fit would otherwise re-wrap a line.)
    function drawable(txt) {
      var tk = units(tokenize(txt));
      var ls = linesFor(tk, customerBreaks(tk));
      var ev = evaluate(ls, ctx, 0, true);
      for (var i = 0; i < ev.violations.length; i++) {
        var x = ev.violations[i];
        if (x === 'overflow' || x === 'too-many-lines') return false;
      }
      return true;
    }
    if (drawable(t)) return t;
    var r = tidy(t, ctx);
    return drawable(r.text) ? r.text : null;
  }

  /**
   * layoutSize(text, ctx) → { size, lines, rewrap }
   * The size (same units as the box) at which every line of `text` stays
   * whole, and whether the cover's height-driven fit would draw it larger
   * — and therefore re-wrap a line into lines nobody chose. The storefront
   * uses it after every write of its own: when `rewrap` is true it sets the
   * customer's size to `size` through the stepper's own door, so the cover
   * draws exactly the lines the field holds. Stelios's «micro auto adjust».
   */
  function layoutSize(text, ctx) {
    var tk = units(tokenize(String(text || '')));
    if (!tk.length) return null;
    var ls = linesFor(tk, customerBreaks(tk));
    var d = dims(ls, ctx);
    var fitSize = Math.min(d.byH, d.ceil);          // what the cover's fit will choose
    var size = Math.min(fitSize, d.byW);            // what keeps every line whole
    return { size: size, lines: ls.map(lineText), rewrap: d.byW < fitSize - 0.01 };
  }

  return {
    tidy: tidy,
    needsTidy: needsTidy,
    accents: accents,
    shapeOf: shapeOf,
    capacity: capacity,
    fitIdea: fitIdea,
    layoutSize: layoutSize,
    // exposed for tests
    tokenize: tokenize,
    units: units,
    sizeFor: sizeFor,
    evaluate: evaluate,
    linesFor: linesFor,
    customerBreaks: customerBreaks,
  };
});
